import localApiSetup from './localApiSetup.js';
import { API_BASE_URL, updateEnvironmentConfig, environmentConfig } from '../config/environment.js';

// Static properties to persist across all instances (survives navigation)
let memoryToken = null;
let memoryUserData = null;

class ApiService {
  constructor() {
    this.pendingRequests = new Map(); // Track pending requests to prevent duplicates
    this.retryDelays = [1000, 2000, 4000]; // Retry delays in milliseconds
    this.baseURL = null; // Will be set dynamically
    this.configLoaded = false;

    // Clear any stored external URLs
    localApiSetup.clearExternalUrls();

    // Initialize base URL
    this.initializeBaseUrl();
  }

  // Getters/setters for memory storage
  get memoryToken() {
    return memoryToken;
  }
  set memoryToken(value) {
    memoryToken = value;
  }
  get memoryUserData() {
    return memoryUserData;
  }
  set memoryUserData(value) {
    memoryUserData = value;
  }

  // Initialize base URL dynamically
  async initializeBaseUrl() {
    try {
      // Wait for environment config to load
      await localApiSetup.loadEnvironmentConfig();

      // Get the current base URL
      const newBaseUrl = localApiSetup.getApiBaseUrl();
      this.baseURL = newBaseUrl;
      this.configLoaded = true;

      updateEnvironmentConfig({ API_BASE_URL: newBaseUrl });
      console.log('🌐 API Service initialized with base URL:', this.baseURL);
      console.log('🌐 Current window location:', {
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        port: window.location.port,
        origin: window.location.origin,
        href: window.location.href
      });
    } catch (error) {
      console.error('❌ Error initializing API base URL:', error);
      // Fallback to default
      this.baseURL = API_BASE_URL;
      this.configLoaded = true;
    }
  }

  // Simple connectivity test
  async testConnectivity() {
    try {
      console.log('🔍 Testing basic connectivity...');
      const response = await fetch(`${environmentConfig.FRONTEND_URL}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Basic connectivity test successful:', data);
        return true;
      } else {
        console.log('❌ Basic connectivity test failed:', response.status);
        return false;
      }
    } catch (error) {
      console.error('❌ Basic connectivity test error:', error.message);
      return false;
    }
  }

  // Health check method
  async healthCheck() {
    try {
      const currentBaseUrl = this.getBaseUrl();
      const healthUrl = currentBaseUrl.replace('/api', '') + '/health';
      console.log('🏥 Health check URL:', healthUrl);

      const response = await fetch(healthUrl, {
        method: 'GET',
        signal: AbortSignal.timeout(3000)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Health check successful:', data);
        return { success: true, data };
      } else {
        console.log('❌ Health check failed:', response.status);
        return { success: false, status: response.status };
      }
    } catch (error) {
      console.error('❌ Health check error:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Clear all pending requests
  clearPendingRequests() {
    console.log('🧹 Clearing all pending requests');
    this.pendingRequests.clear();
  }

  // Clear pending requests for a specific endpoint pattern
  clearPendingRequestsForEndpoint(endpointPattern) {
    console.log(`🧹 Clearing pending requests for endpoint pattern: ${endpointPattern}`);
    const keysToDelete = [];

    for (const [key, value] of this.pendingRequests.entries()) {
      if (key.includes(endpointPattern)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      this.pendingRequests.delete(key);
      console.log(`🧹 Cleared pending request: ${key}`);
    });

    console.log(`🧹 Cleared ${keysToDelete.length} pending requests for pattern: ${endpointPattern}`);
  }

  // Get current base URL (with fallback)
  getBaseUrl() {
    // CRITICAL: For localhost, ALWAYS use /api to ensure proxy works
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('🌐 getBaseUrl: Using proxy for localhost');
      return '/api';
    }

    if (!this.baseURL) {
      // If not initialized yet, use the default
      return API_BASE_URL;
    }
    return this.baseURL;
  }

  // Update base URL (simplified)
  async updateBaseUrl() {
    try {
      const newBaseUrl = localApiSetup.getApiBaseUrl();
      if (newBaseUrl !== this.baseURL) {
        this.baseURL = newBaseUrl;
        updateEnvironmentConfig({ API_BASE_URL: newBaseUrl });
        console.log('🌐 Updated API base URL:', this.baseURL);
      }
    } catch (error) {
      console.error('❌ Error updating base URL:', error);
    }
  }

  // Set base URL (for testing)
  setBaseUrl(url) {
    localApiSetup.setBaseUrl(url);
    this.baseURL = url;
    updateEnvironmentConfig({ API_BASE_URL: url });
    console.log('🌐 Manually set API base URL:', this.baseURL);
  }

  // Enhanced request method with retry logic and deduplication
  async request(endpoint, options = {}, returnBlob = false) {
    console.log('📞 API Service: request method called');
    console.log('📞 API Service: endpoint =', endpoint);
    console.log('📞 API Service: options =', options);

    // Ensure base URL is initialized
    if (!this.configLoaded) {
      console.log('📞 API Service: Initializing base URL in request...');
      await this.initializeBaseUrl();
    }

    const requestKey = `${options.method || 'GET'}_${endpoint}_${JSON.stringify(options.params || {})}`;
    console.log('📞 API Service: requestKey =', requestKey);

    // For mutation requests (POST, PUT, PATCH, DELETE), don't use pending requests to avoid conflicts
    const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase() || 'GET');
    if (isMutation) {
      console.log(`📞 API Service: ${options.method || 'POST'} request - bypassing pending requests cache`);
      return this._makeRequest(endpoint, options, returnBlob);
    }

    // Check if same request is already pending
    if (this.pendingRequests.has(requestKey)) {
      console.log('🔄 Request already pending, returning existing promise');
      try {
        const result = await this.pendingRequests.get(requestKey);
        return result;
      } catch (error) {
        // If the pending request failed, remove it and try again
        console.log('❌ Pending request failed, retrying...');
        this.pendingRequests.delete(requestKey);
      }
    }

    console.log('📞 API Service: Creating new request promise...');
    const requestPromise = this._makeRequest(endpoint, options, returnBlob);
    this.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } catch (error) {
      // Remove failed request from pending list
      this.pendingRequests.delete(requestKey);
      throw error;
    }
  }

  // Internal request method with retry logic
  async _makeRequest(endpoint, options = {}, returnBlob = false, retryCount = 0) {
    // Get current base URL (this ensures we always have the latest)
    const currentBaseUrl = this.getBaseUrl();

    // Enable timeout protection
    const timeout = 15000; // 15 seconds timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log('⏰ Request timeout reached, aborting...');
      controller.abort();
    }, timeout);

    console.log(`🔍 Starting request for endpoint: ${endpoint}`);
    console.log(`🔍 Retry count: ${retryCount}`);
    console.log(`🔍 Current base URL: ${currentBaseUrl}`);

    try {
      // Handle query parameters
      let finalEndpoint = endpoint;
      if (options.params && Object.keys(options.params).length > 0) {
        const params = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value);
          }
        });
        const queryString = params.toString();
        if (queryString) {
          finalEndpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryString}`;
        }
      }

      const url = `${currentBaseUrl}${finalEndpoint}`;
      console.log(`🔍 Full URL being requested: ${url}`);
      console.log(`🔍 Query parameters:`, options.params);

      const requestOptions = {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        signal: controller.signal,
        ...options
      };

      // Add auth token if available
      const token = this.getAuthToken();
      if (token) {
        requestOptions.headers.Authorization = `Bearer ${token}`;
        console.log(`✅ Token added to Authorization header (length: ${token.length})`);
      } else {
        console.warn(`⚠️ No token available for request to: ${endpoint}`);
      }

      console.log(`🌐 Making ${requestOptions.method} request to: ${url}`);
      console.log(`🔍 Backend URL: ${currentBaseUrl}`);
      console.log(`🔍 Token available: ${!!token}`);
      console.log(`🔍 Authorization header: ${requestOptions.headers.Authorization ? 'Present' : 'Missing'}`);
      console.log(`🔍 Request options:`, {
        method: requestOptions.method,
        headers: { ...requestOptions.headers, Authorization: requestOptions.headers.Authorization ? `Bearer ${token?.substring(0, 20)}...` : 'Missing' },
        body: requestOptions.body ? 'present' : 'none'
      });
      console.log(`🔍 Full request URL: ${url}`);
      console.log(`🔍 Endpoint: ${endpoint}`);

      console.log('🚀 About to call fetch...');

      try {
        const response = await fetch(url, requestOptions);
        console.log('✅ Fetch completed!');

        // Clear timeout since request completed
        clearTimeout(timeoutId);

        console.log(`✅ Response received: ${response.status} ${response.statusText}`);
        console.log(`📋 Response headers:`, Object.fromEntries(response.headers.entries()));

        // Handle different response types
        if (returnBlob) {
          return { success: response.ok, data: await response.blob() };
        }

        let data;
        let responseText;

        try {
          // First, get the response text
          responseText = await response.text();
          console.log('📥 Raw response text:', responseText);

          // Check Content-Type header to determine if it's JSON or HTML
          const contentType = response.headers.get('content-type') || '';
          console.log('📋 Content-Type header:', contentType);

          // If Content-Type is HTML (not JSON), then it's an error page
          if (contentType.includes('text/html') && responseText && responseText.includes('<!DOCTYPE html>')) {
            // Handle 404 HTML responses differently - don't clear session
            if (response.status === 404) {
              console.error('🌐 404 HTML response detected - route not found');
              console.error('📋 This usually means:');
              console.error('   1. The route does not exist on the backend');
              console.error('   2. The backend server needs to be restarted');
              console.error('   3. The route path is incorrect');
              // Don't clear session for 404s - this is a route/server issue
              const routeError = `Route not found: ${endpoint}. The server may need to be restarted or the endpoint may not exist.`;
              throw new Error(routeError);
            } else {
              // Other HTML responses might be redirects
              console.error('🌐 HTML response detected (not 404) - might be redirect');
              console.error('📄 First 500 chars of HTML response:', responseText.substring(0, 500));
              console.error('🔍 URL that returned HTML:', `${this.getBaseUrl()}${endpoint}`);
              console.error('⚠️ This usually means the backend server is not running or the endpoint is configured incorrectly');
              console.error('⚠️ Check the backend server logs and ensure the API is running correctly');
              // DON'T clear session - this is likely a backend issue, not an auth issue
              throw new Error('Backend server returned HTML instead of JSON. Please check if the server is running correctly.');
            }
          }

          // Then try to parse it as JSON
          console.log('🔍 About to parse JSON response...');
          console.log('🔍 Response text length:', responseText?.length);
          console.log('🔍 Response text starts with:', responseText?.substring(0, 100));
          data = JSON.parse(responseText);
          console.log('✅ JSON parsed successfully!');
          console.log('✅ Data type:', typeof data);
          console.log('✅ Is array?:', Array.isArray(data));
          console.log('✅ Data keys:', data && typeof data === 'object' ? Object.keys(data) : 'N/A');
        } catch (jsonError) {
          console.error('❌ JSON PARSING FAILED!');
          console.error('❌ Error message:', jsonError.message);
          console.error('❌ Error stack:', jsonError.stack);
          // If we already threw an error for HTML, re-throw it
          if (jsonError.message && (jsonError.message.includes('Route not found') || jsonError.message.includes('Session expired') || jsonError.message.includes('Backend server returned HTML'))) {
            throw jsonError;
          }

          console.error('❌ Failed to parse response as JSON:', jsonError);
          console.error('❌ Response text:', responseText?.substring(0, 200));

          // Check if it's a proxy error or other non-JSON response
          if (responseText && responseText.includes('Proxy error')) {
            console.error('🔗 Proxy error detected - server may be down or unreachable');
            throw new Error('Server connection failed. Please check if the server is running and try again.');
          } else if (!responseText || responseText.trim() === '') {
            console.error('📭 Empty response received');
            throw new Error('Server returned empty response');
          } else {
            console.error('🔧 Invalid JSON response:', responseText.substring(0, 200) + '...');
            throw new Error(`Server returned invalid response: ${response.status} ${response.statusText}`);
          }
        }

        console.log(`📦 Response data:`, data);
        console.log(`📦 Response data type:`, typeof data);
        console.log(`📦 Response data keys:`, data && typeof data === 'object' ? Object.keys(data) : 'not an object');

        // Handle failed responses with proper error messages
        if (!response.ok) {
          console.error(`❌ API request failed: ${response.status} ${response.statusText}`);
          console.error(`❌ Response data:`, data);
          console.error(`❌ Response headers:`, Object.fromEntries(response.headers.entries()));

          // Handle specific error cases
          if (response.status === 401) {
            const errorMessage = data?.message || data?.error || '';
            console.log('🔐 Unauthorized (401) - Error message:', errorMessage);

            // Only clear session if it's actually an authentication issue
            // If it's "Access token required", we might just need to wait for token
            // Don't clear session if the token is just missing (might be loading)
            if (errorMessage.includes('Access token required')) {
              console.warn('⚠️ Access token required - checking token status');
              // Check if we actually have a token
              const token = this.getAuthToken();
              if (!token) {
                console.warn('⚠️ No token found - session might be initializing, NOT clearing session');
                // Don't clear session - might be a race condition or token still loading
              } else {
                // Token exists but backend says "Access token required"
                // This could mean:
                // 1. Token not sent in header (bug in request code)
                // 2. Route doesn't exist (should be 404, not 401)
                // 3. Token format is wrong
                console.warn('⚠️ Token exists but backend says "Access token required" - likely route issue or token not sent properly');
                console.warn('⚠️ NOT clearing session - this might be a route/server issue, not an auth issue');
                // Don't clear session - this might be a bug, not an auth failure
              }
            } else if (errorMessage.includes('expired') || errorMessage.includes('invalid') || errorMessage.includes('malformed')) {
              console.warn('⚠️ Token expired or invalid - clearing session');
              this.clearSession();
            } else {
              // Other 401 errors - clear session as a precaution
              console.warn('⚠️ Other 401 error - clearing session');
              this.clearSession();
            }
          } else if (response.status === 404) {
            // 404 means route not found - don't clear session for route errors
            console.error('❌ Route not found (404) - server may need restart or endpoint does not exist');
            const errorMessage = data?.message || data?.error || `Endpoint not found: ${endpoint}. Server may need restart.`;
            throw new Error(errorMessage);
          }

          // Extract specific error message from backend response
          const errorMessage = data?.message || data?.error || `HTTP ${response.status}: ${response.statusText}`;
          console.error(`❌ Server error message:`, errorMessage);
          throw new Error(errorMessage);
        }

        // Check if backend returned success: false even with 200 status
        if (data.success === false) {
          console.error(`❌ Backend returned success: false`);
          const errorMessage = data.message || 'Request failed';
          throw new Error(errorMessage);
        }

        console.log(`🎉 Request successful!`);
        // Return the data directly since the backend already provides the proper structure
        return data;

      } catch (fetchError) {
        // Clear timeout since request failed
        clearTimeout(timeoutId);

        // If it's already our custom error, re-throw it
        if (fetchError.message && !fetchError.message.includes('Failed to fetch')) {
          throw fetchError;
        }

        // Handle network/fetch errors
        console.error('❌ Fetch error:', fetchError.message);
        throw new Error(fetchError.message || 'Network request failed');
      }
    } catch (error) {
      console.error(`❌ Request failed (attempt ${retryCount + 1}):`, error.message);
      console.error(`❌ Error type:`, error.name);
      console.error(`❌ Error stack:`, error.stack);

      // Handle timeout
      if (error.name === 'AbortError') {
        console.error('⏰ Request timed out');
        throw new Error('Request timed out');
      }

      // Handle network errors
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.error('🌐 Network error detected');
        if (retryCount < this.retryDelays.length) {
          const delay = this.retryDelays[retryCount];
          console.log(`🔄 Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this._makeRequest(endpoint, options, returnBlob, retryCount + 1);
        }
      }

      throw error;
    }
  }

  // Health check with retry
  async getHealth() {
    // Ensure initialization before health check
    if (!this.configLoaded) {
      await this.initializeBaseUrl();
    }
    return this.request('/health');
  }

  // Authentication methods
  async login(email, password) {
    console.log('🔐 API Service: login method called');
    console.log('🔐 API Service: email =', email);
    console.log('🔐 API Service: password =', password ? '[HIDDEN]' : 'undefined');
    console.log('🔐 API Service: Current base URL =', this.getBaseUrl());

    // Log the exact payload being sent
    const loginPayload = { email, password };
    console.log('📤 Sending login request with:', {
      email: loginPayload.email,
      password: loginPayload.password ? '[HIDDEN]' : 'undefined'
    });

    // Ensure initialization before login
    if (!this.configLoaded) {
      console.log('🔐 API Service: Initializing base URL...');
      await this.initializeBaseUrl();
    }

    console.log('🔐 API Service: About to call request method...');
    console.log('🔐 API Service: Full URL will be =', `${this.getBaseUrl()}/auth/login`);

    try {
      const response = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify(loginPayload),
      });
      console.log('✅ Login successful:', response);
      return response;
    } catch (error) {
      console.error('❌ Login failed:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // DEPRECATED: Old registration methods removed - use registerSecure instead
  async registerSuperAdmin(userData) {
    throw new Error('This registration method is no longer available. Please use secure registration with @rannkly.com email verification.');
  }

  async registerAdmin(userData) {
    throw new Error('This registration method is no longer available. Please use secure registration with @rannkly.com email verification.');
  }

  async registerUser(userData) {
    throw new Error('This registration method is no longer available. Please use secure registration with @rannkly.com email verification.');
  }

  // New secure registration with OTP verification
  async registerSecure(userData) {
    return this.request('/auth/register/secure', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async verifyOTP(userId, otp) {
    return this.request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ userId, otp }),
    });
  }

  async resendOTP(userId) {
    return this.request('/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async logout() {
    try {
      console.log('🔍 Attempting logout API call...');
      const result = await this.request('/auth/logout', {
        method: 'POST',
      });

      console.log('✅ Logout API call successful');

      // Clear session data regardless of API response
      this.clearSession();

      return result;
    } catch (error) {
      console.error('❌ Logout API error:', error);

      // Clear session data even if API call fails
      this.clearSession();

      // Return a success response to prevent UI issues
      return {
        success: true,
        message: 'Logout completed (local cleanup)'
      };
    }
  }

  async getProfile() {
    // Don't add Authorization header manually - _makeRequest handles it
    return this.request('/auth/profile');
  }

  // Refresh user data from server (bypass cache)
  async refreshUserData() {
    try {
      console.log('🔄 Refreshing user data from server...');

      // Make request with cache-busting timestamp to ensure fresh data
      // Use a random query param to ensure browser and server don't cache
      const cacheBuster = `t=${Date.now()}&r=${Math.random()}`;
      const response = await this.request(`/auth/profile?${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
      });

      console.log('📊 Refresh response:', response);

      if (response && response.data) {
        const userData = response.data;

        console.log('📊 Raw user data from server:', {
          email: userData.email,
          permissionsCount: userData.permissions?.length || 0,
          permissions: userData.permissions
        });

        // Store fresh user data with all fields (overwrite directly, don't remove first)
        const fullUserData = {
          id: userData.id || userData._id,
          _id: userData.id || userData._id,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          department: userData.department,
          permissions: [...(userData.permissions || [])], // Create new array reference
          lastLogin: userData.lastLogin,
          status: userData.status,
          role: userData.role,
          _refreshKey: Date.now() // Add refresh key for React to detect change
        };

        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(fullUserData));
        if (userData.email) {
          localStorage.setItem(`user_${userData.email}`, JSON.stringify(fullUserData));
        }

        console.log('✅ User data refreshed successfully');
        console.log('📊 User permissions:', fullUserData.permissions?.length || 0, 'permissions');
        console.log('📊 Has manage_system_settings:', fullUserData.permissions?.includes('manage_system_settings'));
        console.log('📊 Has edit_system_settings:', fullUserData.permissions?.includes('edit_system_settings'));
        console.log('📊 Has view_unassigned_tickets:', fullUserData.permissions?.includes('view_unassigned_tickets'));
        console.log('📊 Full permissions list:', fullUserData.permissions);

        return fullUserData;
      } else if (response.success && response.data) {
        // Handle response structure variation
        const userData = response.data;
        const fullUserData = {
          ...userData,
          permissions: [...(userData.permissions || [])], // Create new array reference
          _refreshKey: Date.now()
        };
        localStorage.setItem('user', JSON.stringify(fullUserData));
        if (userData.email) {
          localStorage.setItem(`user_${userData.email}`, JSON.stringify(fullUserData));
        }
        return fullUserData;
      }

      console.warn('⚠️ Unexpected response format:', response);
      return null;
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
      return null;
    }
  }

  async updateProfile(userData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(userData),
    });
  }

  // Forgot Password
  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Reset Password
  async resetPassword(token, password) {
    return this.request(`/auth/reset-password/${token}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  // Verify Reset Token
  async verifyResetToken(token) {
    return this.request(`/auth/verify-reset-token/${token}`, {
      method: 'GET',
    });
  }

  async changePassword(userId, currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      headers: {
        'user-id': userId,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // User management methods
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';
    return this.request(endpoint);
  }

  async getUserById(userId) {
    return this.request(`/users/${userId}`);
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async updateUserStatus(userId, status) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateUserPassword(userId, currentPassword, newPassword) {
    return this.request(`/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async deleteMultipleUsers(userIds) {
    return this.request('/users/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ userIds }),
    });
  }

  // Statistics methods
  async getUserStats() {
    return this.request('/users/stats/roles');
  }

  async getRecentUserStats() {
    return this.request('/users/stats/recent');
  }

  // Ticket statistics
  async getTicketStats() {
    return this.request('/stats/overview');
  }

  async getSourceTagsStats() {
    return this.request('/tickets/stats/by-source');
  }

  async getUserPerformance() {
    return this.request('/users/performance');
  }

  // Permission management methods
  async getAvailablePermissions() {
    return this.request('/permissions/available');
  }

  async getUserPermissions(userId) {
    return this.request(`/permissions/user/${userId}`);
  }

  async addUserPermissions(userId, permissions) {
    return this.request(`/permissions/user/${userId}/add`, {
      method: 'POST',
      body: JSON.stringify({ permissions }),
    });
  }

  async removeUserPermissions(userId, permissions) {
    return this.request(`/permissions/user/${userId}/remove`, {
      method: 'POST',
      body: JSON.stringify({ permissions }),
    });
  }

  async setUserPermissions(userId, permissions) {
    return this.request(`/permissions/user/${userId}/set`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
  }

  async getUsersByPermission(permission) {
    return this.request(`/permissions/users/by-permission/${permission}`);
  }

  async getPermissionStats() {
    return this.request('/permissions/stats');
  }

  // Role management methods
  async promoteUser(userId, reason = '') {
    return this.request(`/roles/promote/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async demoteAdmin(userId, reason = '') {
    return this.request(`/roles/demote/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async getRoleHistory(userId, limit = 50) {
    return this.request(`/roles/history/${userId}?limit=${limit}`);
  }

  async getUserActivities(userId, limit = 50, activityType = null) {
    const params = new URLSearchParams({ limit });
    if (activityType) params.append('activityType', activityType);
    return this.request(`/roles/activities/${userId}?${params}`);
  }

  async getUserActivitiesWithTicketsAndTodos(userId, limit = 50) {
    return this.request(`/users/${userId}/activities?limit=${limit}`);
  }

  async getPromotableUsers() {
    return this.request('/roles/promotable');
  }

  async getDemotableAdmins() {
    return this.request('/roles/demotable');
  }

  // Email fetch methods
  async triggerEmailFetch() {
    return this.request('/auth/trigger-email-fetch', {
      method: 'POST',
    });
  }

  async createTicket(ticketData) {
    return this.request('/tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData)
    });
  }

  // Ticket assignment methods
  async getAssignableUsers() {
    return this.request('/tickets/assignable-users');
  }

  async getAssignableUsersForTicket(ticketId) {
    return this.request(`/tickets/${ticketId}/assignable-users`);
  }

  async getEscalatedTicketsToMe() {
    return this.request('/tickets/escalated-to-me');
  }

  async assignTicket(ticketId, assignedTo, reason = 'manual') {
    return this.request(`/tickets/${ticketId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assignedTo, reason }),
    });
  }

  async escalateTicket(ticketId, escalatedTo, escalationReason = '') {
    console.log('🔄 API: Escalating ticket', ticketId, 'to', escalatedTo);
    console.log('📝 Escalation reason:', escalationReason);

    const response = await this.request(`/tickets/${ticketId}/escalate`, {
      method: 'POST',
      body: JSON.stringify({ escalatedTo, escalationReason })
    });

    console.log('✅ Escalation API response:', response);
    return response;
  }

  async updateTicket(ticketId, updates) {
    return this.request(`/tickets/${ticketId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async updateTicketStatus(ticketId, status, resolution = '') {
    console.log('🔍 updateTicketStatus called with:', { ticketId, status, resolution });
    console.log('🔍 Status value being sent:', status);
    console.log('🔍 Status type:', typeof status);
    console.log('🔍 Status length:', status ? status.length : 'undefined');
    console.log('🔍 JSON body being sent:', JSON.stringify({ status, resolution }));

    const response = await this.request(`/tickets/${ticketId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, resolution })
    });
    console.log('🔍 updateTicketStatus response:', response);
    return response;
  }

  // Close ticket with email preview
  async closeTicketWithEmail(ticketId, emailData) {
    console.log('📧 closeTicketWithEmail called with:', { ticketId, emailData });

    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }

    const response = await this.request(`/tickets/${ticketId}/close-with-email`, {
      method: 'POST',
      body: JSON.stringify(emailData)
    });
    console.log('📧 closeTicketWithEmail response:', response);
    return response;
  }

  // Get all support person ratings
  async getSupportPersonRatings() {
    const response = await this.request('/ratings/support-stats');
    return response;
  }

  // Get specific support person rating stats
  async getSupportPersonRatingStats(userId) {
    const response = await this.request(`/ratings/support-stats/${userId}`);
    return response;
  }

  // Get tickets with customer ratings
  async getRatedTickets(page = 1, limit = 20, rating = null, supportPerson = null) {
    let url = `/ratings/tickets?page=${page}&limit=${limit}`;
    if (rating) url += `&rating=${rating}`;
    if (supportPerson) url += `&supportPerson=${supportPerson}`;

    const response = await this.request(url);
    return response;
  }

  // Get rating summary statistics
  async getRatingSummary() {
    const response = await this.request('/ratings/summary');
    return response;
  }

  // Get top performing support personnel
  async getTopPerformers(limit = 10) {
    const response = await this.request(`/ratings/top-performers?limit=${limit}`);
    return response;
  }

  // Update ticket rating manually
  async updateTicketRating(ticketId, rating, reason = '') {
    const response = await this.request(`/ratings/tickets/${ticketId}/rating`, {
      method: 'PUT',
      body: JSON.stringify({ rating, reason })
    });
    return response;
  }

  // Delete ticket (restricted to Super Admin and Admin roles only)
  async deleteTicket(ticketId) {
    console.log('🗑️ API: Deleting ticket', ticketId);

    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }

    const response = await this.request(`/tickets/${ticketId}`, {
      method: 'DELETE'
    });

    console.log('🗑️ deleteTicket response:', response);
    return response;
  }

  // Track response to ticket
  async trackTicketResponse(ticketId, responseType = 'email') {
    console.log('📧 API: Tracking ticket response', { ticketId, responseType });

    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }

    const response = await this.request(`/tickets/${ticketId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ responseType })
    });

    console.log('📧 trackTicketResponse response:', response);
    return response;
  }

  // Get email chain analytics for a ticket
  async getEmailChainAnalytics(ticketId) {
    console.log('📊 API: Getting email chain analytics', { ticketId });

    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }

    const response = await this.request(`/tickets/${ticketId}/email-chain-analytics`);

    console.log('📊 getEmailChainAnalytics response:', response);
    return response;
  }

  // Notification methods
  async getNotifications(params = {}) {
    return this.request('/notifications', { params });
  }

  async getUnreadNotificationCount() {
    return this.request('/notifications/unread-count');
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/notifications/mark-all-read', {
      method: 'PUT'
    });
  }

  async deleteNotification(notificationId) {
    return this.request(`/notifications/${notificationId}`, {
      method: 'DELETE'
    });
  }

  async deleteReadNotifications() {
    return this.request('/notifications/delete-read', {
      method: 'DELETE'
    });
  }

  async getNotificationStats() {
    return this.request('/notifications/stats');
  }

  // Get today's tickets
  async getTodayTickets() {
    console.log('🔍 getTodayTickets called');

    // Get current date in local timezone
    const now = new Date();

    // Create start of day 24 hours ago (to show recent tickets)
    const startOfRecentPeriod = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Create end of day in local timezone
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    console.log('🔍 Now (local):', now.toLocaleString());
    console.log('🔍 Start of recent period (24 hours ago):', startOfRecentPeriod.toLocaleString());
    console.log('🔍 End of day (local):', endOfDay.toLocaleString());
    console.log('🔍 Start of recent period (ISO):', startOfRecentPeriod.toISOString());
    console.log('🔍 End of day (ISO):', endOfDay.toISOString());

    const params = new URLSearchParams({
      startDate: startOfRecentPeriod.toISOString(),
      endDate: endOfDay.toISOString()
    });

    console.log('🔍 Params:', params.toString());
    console.log('🔍 About to make request to /tickets with params');
    const result = await this.request(`/tickets?${params}`);
    console.log('🔍 getTodayTickets result:', result);
    return result;
  }



  // Helper method to get time ago
  getTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  }

  // Helper method to compare time strings
  isMoreRecent(time1, time2) {
    const getHours = (timeStr) => {
      if (timeStr === 'Just now') return 0;
      const match = timeStr.match(/(\d+)/);
      return match ? parseInt(match[1]) : 999;
    };

    return getHours(time1) < getHours(time2);
  }



  // Calendar management methods
  async getCalendarEvents(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/calendar?${queryString}` : '/calendar';
    return this.request(endpoint);
  }

  async syncCalendar() {
    return this.request('/calendar/sync', {
      method: 'POST'
    });
  }

  async getCalendarEventById(eventId) {
    return this.request(`/calendar/${eventId}`);
  }

  async createCalendarEvent(eventData) {
    return this.request('/calendar', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  async updateCalendarEvent(eventId, eventData) {
    return this.request(`/calendar/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
  }

  async deleteCalendarEvent(eventId) {
    return this.request(`/calendar/${eventId}`, {
      method: 'DELETE',
    });
  }

  async updateEventStatus(eventId, status) {
    return this.request(`/calendar/${eventId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getEventsByDateRange(startDate, endDate, userId = null) {
    const params = new URLSearchParams({ startDate, endDate });
    if (userId) params.append('userId', userId);
    return this.request(`/calendar/range?${params}`);
  }

  async getUpcomingEvents(limit = 10, userId = null) {
    const params = new URLSearchParams({ limit });
    if (userId) params.append('userId', userId);
    return this.request(`/calendar/upcoming?${params}`);
  }

  async addActionItem(eventId, actionItemData) {
    return this.request(`/calendar/${eventId}/action-items`, {
      method: 'POST',
      body: JSON.stringify(actionItemData),
    });
  }

  async updateActionItem(eventId, actionItemId, actionItemData) {
    return this.request(`/calendar/${eventId}/action-items/${actionItemId}`, {
      method: 'PATCH',
      body: JSON.stringify(actionItemData),
    });
  }

  async getCalendarStats(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/calendar/stats/overview?${queryString}` : '/calendar/stats/overview';
    return this.request(endpoint);
  }

  // Utility methods - TRUE MULTI-USER SESSION ISOLATION
  setAuthToken(token, userData = null) {
    console.log('🔐 setAuthToken called:', {
      hasToken: !!token,
      tokenLength: token?.length,
      hasUserData: !!userData,
      userEmail: userData?.email
    });

    // Trim token to remove any whitespace before storing
    const trimmedToken = token ? token.trim() : token;

    // Store in module-level memory as fallback (survives navigation)
    console.log('💾 Storing token in module-level memory...');
    memoryToken = trimmedToken;
    if (userData) {
      memoryUserData = userData;
      console.log('💾 Stored user data in module-level memory for:', userData.email);
    }

    // Clear any old generic token first to prevent conflicts
    localStorage.removeItem('token');

    // Get user data from parameter or localStorage
    let user = userData;
    if (!user) {
      const storedUserData = localStorage.getItem('user');
      if (storedUserData) {
        try {
          user = JSON.parse(storedUserData);
          console.log('📥 Loaded user data from localStorage:', user.email);
        } catch (error) {
          console.error('❌ Error parsing user data:', error);
        }
      }
    }

    if (user && user.email) {
      // Store token with user-specific key
      const tokenKey = `authToken_${user.email}`;
      console.log(`💾 Writing token to localStorage with key: ${tokenKey}`);
      localStorage.setItem(tokenKey, trimmedToken);

      // Verify it was written
      const verifyToken = localStorage.getItem(tokenKey);
      console.log(`✅ Token written verification: ${!!verifyToken}, length: ${verifyToken?.length}`);

      // Also store user data if provided
      if (userData) {
        const userDataString = JSON.stringify(userData);
        localStorage.setItem('user', userDataString);
        localStorage.setItem(`user_${user.email}`, userDataString);
        console.log(`💾 Wrote user data to localStorage`);
      }
      console.log(`✅ Set user-specific token for: ${user.email}`);

      // Log all localStorage keys for debugging
      console.log('📋 All localStorage keys:', Object.keys(localStorage));
    } else {
      // Only use global token if no user data is available
      console.log('⚠️ No user email found, using global authToken key');
      localStorage.setItem('authToken', trimmedToken);
      console.log('⚠️ No user data available, using global token');
    }
  }

  getAuthToken() {
    // PRIORITY 1: Check module-level memory first (survives all navigation, most reliable)
    if (memoryToken && this.isValidToken(memoryToken)) {
      console.log('✅ getAuthToken: Using module-level memory token');
      return memoryToken.trim();
    }

    // PRIORITY 2: Check for ANY authToken in localStorage (don't require user data)
    console.log('🔍 getAuthToken: Module memory token not found, searching localStorage...');

    // Search for user-specific auth tokens first
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('authToken_')) {
        const token = localStorage.getItem(key);
        if (token && this.isValidToken(token)) {
          const email = key.replace('authToken_', '');
          console.log(`✅ getAuthToken: Found valid user token for ${email}, storing in module memory`);
          // Store in module-level memory for next time
          memoryToken = token;
          // Try to load user data if available
          const userData = localStorage.getItem(`user_${email}`) || localStorage.getItem('user');
          if (userData) {
            try {
              memoryUserData = JSON.parse(userData);
            } catch (e) {
              console.error('Error parsing user data:', e);
            }
          }
          return token.trim();
        }
      }
    }

    // PRIORITY 3: Check global authToken
    const globalToken = localStorage.getItem('authToken');
    if (globalToken && this.isValidToken(globalToken)) {
      console.log('✅ getAuthToken: Found valid global authToken, storing in module memory');
      memoryToken = globalToken;
      return globalToken.trim();
    }

    // PRIORITY 4: Try to get user data and associated token
    let userData = localStorage.getItem('user');
    console.log('🔍 getAuthToken: Checking user data approach, userData found:', !!userData);

    // If no user data in localStorage, try to find it from any user_* key
    if (!userData) {
      console.log('🔍 getAuthToken: No user data in localStorage, searching for user_* keys...');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('user_') && key !== 'user_') {
          try {
            const potentialUserData = localStorage.getItem(key);
            if (potentialUserData) {
              const user = JSON.parse(potentialUserData);
              if (user && user.email) {
                console.log(`✅ getAuthToken: Found user data in ${key}, restoring to localStorage`);
                userData = potentialUserData;
                localStorage.setItem('user', userData);
                break;
              }
            }
          } catch (error) {
            // Continue searching
          }
        }
      }
    }

    // If still no user data, search for any authToken and try to infer user from it
    if (!userData) {
      console.log('🔍 getAuthToken: Still no user data, searching for any authToken keys...');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('authToken_')) {
          const email = key.replace('authToken_', '');
          const token = localStorage.getItem(key);
          if (token && this.isValidToken(token)) {
            // Try to find user data for this email
            const userDataForKey = localStorage.getItem(`user_${email}`);
            if (userDataForKey) {
              console.log(`✅ getAuthToken: Found token and user data for ${email}, restoring to localStorage`);
              localStorage.setItem('user', userDataForKey);
              return token.trim();
            }
          }
        }
      }
    }

    if (userData) {
      try {
        const user = JSON.parse(userData);
        console.log('🔍 getAuthToken: user email:', user.email);

        // Try user-specific token first
        const userSpecificToken = localStorage.getItem(`authToken_${user.email}`);
        console.log('🔍 getAuthToken: user-specific token found:', !!userSpecificToken);

        if (userSpecificToken && this.isValidToken(userSpecificToken)) {
          console.log('✅ getAuthToken: Returning user-specific token');
          return userSpecificToken.trim();
        }
      } catch (error) {
        console.error('❌ Error getting user-specific token:', error);
      }
    }

    // Search for any valid authToken keys (user-specific tokens first)
    console.log('🔍 getAuthToken: Searching for user-specific authToken keys...');
    let foundValidToken = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('authToken')) {
        const foundToken = localStorage.getItem(key);
        if (foundToken && this.isValidToken(foundToken)) {
          // If it's a user-specific token, prefer it
          if (key.startsWith('authToken_') && key !== 'authToken') {
            const email = key.replace('authToken_', '');
            // Try to restore user data if we have the email
            const userDataForEmail = localStorage.getItem(`user_${email}`);
            if (userDataForEmail) {
              console.log(`✅ getAuthToken: Found user-specific token for ${email}, restoring user data`);
              localStorage.setItem('user', userDataForEmail);
            }
            console.log(`✅ getAuthToken: Found valid user-specific token in ${key}`, 'success');
            return foundToken.trim();
          } else {
            // Store as fallback
            foundValidToken = foundToken;
          }
        }
      }
    }

    // If we found a valid token (even if not user-specific), use it as last resort
    if (foundValidToken) {
      console.log('✅ getAuthToken: Found valid fallback token', 'success');
      return foundValidToken.trim();
    }

    // Last resort: Try generic "token" key (but warn if no user data)
    console.log('🔍 getAuthToken: Searching for generic "token" key...');
    const altToken = localStorage.getItem('token');
    if (altToken && this.isValidToken(altToken)) {
      // Only use generic token if we have user data, otherwise it might be stale
      if (userData) {
        console.log('✅ getAuthToken: Found valid token in "token" (with user data)', 'success');
        return altToken.trim();
      } else {
        console.warn('⚠️ getAuthToken: Found token in "token" but no user data - token may be stale');
        // Clean up stale token
        localStorage.removeItem('token');
      }
    }

    // If we have user data, try user-specific alternative token
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const userSpecificAltToken = localStorage.getItem(`token_${user.email}`);
        if (userSpecificAltToken && this.isValidToken(userSpecificAltToken)) {
          console.log(`✅ getAuthToken: Found valid token in "token_${user.email}"`, 'success');
          return userSpecificAltToken.trim();
        }
      } catch (error) {
        console.error('❌ Error getting user-specific alt token:', error);
      }
    }

    console.log('🔍 getAuthToken: No valid token found, returning null');

    // Don't force redirect here - let SessionContext handle it
    // This prevents infinite redirect loops
    return null;
  }

  // Helper method to validate token format
  isValidToken(token) {
    if (!token || typeof token !== 'string') {
      return false;
    }

    // Check if token has the correct JWT format (3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.log('❌ Token format invalid: wrong number of parts');
      return false;
    }

    // Check if all parts are non-empty
    if (parts.some(part => !part || part.trim() === '')) {
      console.log('❌ Token format invalid: empty parts');
      return false;
    }

    // Basic length check for JWT parts
    if (parts[0].length < 10 || parts[1].length < 10 || parts[2].length < 10) {
      console.log('❌ Token format invalid: parts too short');
      return false;
    }

    return true;
  }

  removeAuthToken() {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        // Only remove this user's token
        localStorage.removeItem(`authToken_${user.email}`);
        console.log(`✅ Removed user-specific token for: ${user.email}`);
      } catch (error) {
        console.error('❌ Error removing user-specific token:', error);
        // Only remove global token if user parsing fails
        localStorage.removeItem('authToken');
      }
    } else {
      // Only remove global token if no user data
      localStorage.removeItem('authToken');
    }
  }

  isAuthenticated() {
    const token = this.getAuthToken();
    if (!token) {
      console.log('❌ No authentication token found');
      return false;
    }

    // Check if token is expired
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const now = Date.now() / 1000;
        if (payload.exp < now) {
          console.log('❌ Token is expired');
          // Don't remove token immediately - let validateSession handle it
          // This allows validateSession to attempt refresh or proper logout
          return false;
        }
      }
    } catch (error) {
      console.error('❌ Error checking token expiration:', error);
      return false;
    }

    return true;
  }

  // Session management methods - TRUE MULTI-USER ISOLATION
  setLastValidation() {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        // Store validation time with user-specific key
        localStorage.setItem(`lastAuthValidation_${user.email}`, Date.now().toString());
        console.log(`✅ Set user-specific validation time for: ${user.email}`);
      } catch (error) {
        console.error('❌ Error setting user-specific validation:', error);
        localStorage.setItem('lastAuthValidation', Date.now().toString());
      }
    } else {
      localStorage.setItem('lastAuthValidation', Date.now().toString());
    }
  }

  getLastValidation() {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        // Get user-specific validation time
        const userSpecificValidation = localStorage.getItem(`lastAuthValidation_${user.email}`);
        if (userSpecificValidation) {
          console.log(`✅ Got user-specific validation time for: ${user.email}`);
          return userSpecificValidation;
        }
      } catch (error) {
        console.error('❌ Error getting user-specific validation:', error);
      }
    }

    // Fallback to global validation time
    return localStorage.getItem('lastAuthValidation');
  }

  isValidationExpired() {
    const lastValidation = this.getLastValidation();
    if (!lastValidation) return true;

    const now = Date.now();
    const tenMinutes = 10 * 60 * 1000;
    return (now - parseInt(lastValidation)) > tenMinutes;
  }

  clearSession() {
    // Clear module-level memory tokens first
    memoryToken = null;
    memoryUserData = null;

    // Get current user data before clearing anything
    const userData = localStorage.getItem('user');
    let userEmail = null;

    if (userData) {
      try {
        const user = JSON.parse(userData);
        userEmail = user.email;
        console.log(`🔍 Clearing session for user: ${userEmail}`);

        // Clear ONLY this user's session data - DON'T touch other users
        localStorage.removeItem(`user_${userEmail}`);
        localStorage.removeItem(`lastAuthValidation_${userEmail}`);
        localStorage.removeItem(`authToken_${userEmail}`);
        localStorage.removeItem(`token_${userEmail}`);
        console.log(`✅ Cleared user-specific session data for: ${userEmail}`);
      } catch (error) {
        console.error('❌ Error parsing user data during session clear:', error);
      }
    }

    // Clear general user data and generic token keys
    localStorage.removeItem('user');
    localStorage.removeItem('lastAuthValidation');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token'); // Also clear generic token key

    console.log(`✅ Session cleared for user: ${userEmail || 'unknown'}`);
  }

  // Enhanced session validation
  async validateSession() {
    console.log('🔍 API Service: Validating session...');

    // First, try to restore user data if it's missing
    let userData = localStorage.getItem('user');
    if (!userData || userData === 'undefined' || userData === 'null') {
      // Try to find user data in user_* keys
      console.log('🔍 API Service: No user data in localStorage, searching for user_* keys...');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('user_') && key !== 'user_') {
          try {
            const potentialUserData = localStorage.getItem(key);
            if (potentialUserData) {
              const user = JSON.parse(potentialUserData);
              if (user && user.email) {
                console.log(`✅ API Service: Found user data in ${key}, restoring to localStorage`);
                userData = potentialUserData;
                localStorage.setItem('user', userData);
                break;
              }
            }
          } catch (error) {
            // Continue searching
          }
        }
      }
    }

    // Check if we have basic session data
    const token = this.getAuthToken();
    if (!token) {
      console.log('❌ API Service: No token found');
      // If we have user data but no token, the token might have expired
      // Clear the session to force re-login
      if (userData) {
        console.log('❌ API Service: User data exists but no token, clearing session');
        this.clearSession();
      }
      return false;
    }

    // Check if token is expired
    let isTokenExpired = false;
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const now = Date.now() / 1000;
        if (payload.exp < now) {
          console.log('❌ API Service: Token is expired');
          isTokenExpired = true;
        }
      }
    } catch (error) {
      console.error('❌ API Service: Error checking token expiration:', error);
    }

    if (!userData || userData === 'undefined' || userData === 'null') {
      console.log('❌ API Service: No valid user data found');
      if (isTokenExpired) {
        console.log('❌ API Service: Token expired and no user data, clearing session');
        this.clearSession();
      }
      return false;
    }

    try {
      const user = JSON.parse(userData);
      if (!user || !user.email) {
        console.log('❌ API Service: Invalid user data structure');
        if (isTokenExpired) {
          this.clearSession();
        }
        return false;
      }
    } catch (error) {
      console.log('❌ API Service: Failed to parse user data');
      if (isTokenExpired) {
        this.clearSession();
      }
      return false;
    }

    // If token is expired, try to validate with server first
    // The server will return 401 if token is truly expired
    // Always perform fresh validation - don't use cache for permissions
    // This ensures permission changes are detected immediately
    try {
      console.log('🔍 API Service: Performing fresh API validation...');
      await this.getProfile();
      this.setLastValidation();
      console.log('✅ API Service: API validation successful');
      return true;
    } catch (error) {
      console.error('❌ API Service: API validation failed:', error);

      // Check error message more carefully
      const errorMsg = error.message || '';

      // Only clear session for confirmed auth failures (not 404s or network errors)
      if (errorMsg.includes('Session expired') ||
        errorMsg.includes('Token expired') ||
        errorMsg.includes('Invalid token') ||
        errorMsg.includes('Unauthorized') ||
        errorMsg.includes('Access token required') ||
        (errorMsg.includes('401') && !errorMsg.includes('Route not found')) ||
        (errorMsg.includes('403') && !errorMsg.includes('Route not found'))) {
        console.log('❌ API Service: Critical auth error, clearing session');
        this.clearSession();
        return false;
      } else {
        // For network errors, 404s, or other non-auth errors, keep session
        console.log('⚠️ API Service: Non-critical error, keeping session:', errorMsg.substring(0, 100));
        this.setLastValidation();
        return true; // Return true to keep session alive
      }
    }
  }

  // Todo methods
  async getTodos(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        queryParams.append(key, filters[key]);
      }
    });
    return this.request(`/todos?${queryParams}`);
  }

  async getTodo(id) {
    return this.request(`/todos/${id}`);
  }

  async createTodo(todoData) {
    return this.request('/todos', {
      method: 'POST',
      body: JSON.stringify(todoData)
    });
  }

  async updateTodo(id, todoData) {
    return this.request(`/todos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(todoData)
    });
  }

  async deleteTodo(id) {
    return this.request(`/todos/${id}`, {
      method: 'DELETE'
    });
  }

  async toggleTodo(id) {
    return this.request(`/todos/${id}/toggle`, {
      method: 'PATCH'
    });
  }

  async completeTodo(id) {
    return this.request(`/todos/${id}/complete`, {
      method: 'PATCH'
    });
  }

  async incompleteTodo(id) {
    return this.request(`/todos/${id}/incomplete`, {
      method: 'PATCH'
    });
  }

  // Feedback methods
  async getFeedback(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams.append(key, params[key]);
      }
    });
    return this.request(`/feedback?${queryParams}`);
  }

  async getFeedbackById(feedbackId) {
    return this.request(`/feedback/${feedbackId}`);
  }

  async createFeedback(feedbackData) {
    return this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(feedbackData)
    });
  }

  async updateFeedback(feedbackId, feedbackData) {
    return this.request(`/feedback/${feedbackId}`, {
      method: 'PUT',
      body: JSON.stringify(feedbackData)
    });
  }

  async deleteFeedback(feedbackId) {
    return this.request(`/feedback/${feedbackId}`, {
      method: 'DELETE'
    });
  }

  async resolveFeedback(feedbackId, resolutionNotes = '') {
    return this.request(`/feedback/${feedbackId}/resolve`, {
      method: 'PATCH',
      body: JSON.stringify({ resolutionNotes })
    });
  }

  async escalateFeedback(feedbackId, escalatedTo, escalationReason = '') {
    return this.request(`/feedback/${feedbackId}/escalate`, {
      method: 'PATCH',
      body: JSON.stringify({ escalatedTo, escalationReason })
    });
  }

  async closeFeedback(feedbackId, resolution = '') {
    return this.request(`/feedback/${feedbackId}/close`, {
      method: 'POST',
      body: JSON.stringify({ resolution })
    });
  }

  async getFeedbackByClient(clientId, params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams.append(key, params[key]);
      }
    });
    return this.request(`/feedback/client/${clientId}?${queryParams}`);
  }

  async getFeedbackStats(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams.append(key, params[key]);
      }
    });
    return this.request(`/feedback/stats/overview?${queryParams}`);
  }

  async getClientFeedbackList(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams.append(key, params[key]);
      }
    });
    return this.request(`/feedback/clients?${queryParams}`);
  }

  async getClientFeedbackAnalytics(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams.append(key, params[key]);
      }
    });
    return this.request(`/feedback/analytics/clients?${queryParams}`);
  }

  async getClientAnalytics(clientId, params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams.append(key, params[key]);
      }
    });
    return this.request(`/clients/${clientId}/analytics?${queryParams}`);
  }

  async getBulkClientAnalytics(clientIds, options = {}) {
    return this.request('/clients/analytics/bulk', {
      method: 'POST',
      body: JSON.stringify({ clientIds, options })
    });
  }

  async recordFeedbackAttempt(clientId, attemptData) {
    return this.request(`/feedback/attempts/${clientId}`, {
      method: 'POST',
      body: JSON.stringify(attemptData)
    });
  }

  // System Settings API methods
  async getSystemSettings() {
    return this.request('/config');
  }

  async updateEscalationSettings(settings) {
    return this.request('/config/escalation', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  async updateRoundRobinSettings(settings) {
    return this.request('/config/round-robin', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  async updateNotificationSettings(settings) {
    return this.request('/config/notifications', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  async updateAllSettings(settings) {
    return this.request('/config/all', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  async resetSystemSettings() {
    return this.request('/config/reset', {
      method: 'POST'
    });
  }

  // Client Management API Methods
  async getClients(params = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return this.request(`/clients?${queryParams}`);
  }

  async getAllClients(params = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return this.request(`/clients/all?${queryParams}`);
  }

  async getPocNames() {
    return this.request('/clients/poc-names');
  }

  async getClientById(clientId) {
    return this.request(`/clients/${clientId}`);
  }

  async createClient(clientData) {
    return this.request('/clients', {
      method: 'POST',
      body: JSON.stringify(clientData)
    });
  }

  async updateClient(clientId, clientData) {
    console.log('📡 [JOURNEY DEBUG] API.updateClient called');
    console.log('📡 [JOURNEY DEBUG] Client ID:', clientId);
    console.log('📡 [JOURNEY DEBUG] Client Data:', JSON.stringify(clientData, null, 2));

    const result = await this.request(`/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(clientData)
    });

    console.log('📡 [JOURNEY DEBUG] API.updateClient result:', JSON.stringify(result, null, 2));
    return result;
  }

  async deleteClient(clientId) {
    return this.request(`/clients/${clientId}`, {
      method: 'DELETE'
    });
  }

  async removeClientEmail(clientId, emailIndex) {
    return this.request(`/clients/${clientId}/emails/${emailIndex}`, {
      method: 'DELETE'
    });
  }

  async getClientStats() {
    return this.request('/clients/stats/overview');
  }

  async importClients(clients) {
    return this.request('/clients/import', {
      method: 'POST',
      body: JSON.stringify({ clients })
    });
  }

  async mergeClients(sourceClientId, targetClientId, mergeOptions) {
    return this.request('/clients/merge', {
      method: 'POST',
      body: JSON.stringify({ sourceClientId, targetClientId, mergeOptions })
    });
  }

  // Miscellaneous Tickets API Methods
  async getMiscellaneousTickets(params = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return this.request(`/tickets/miscellaneous?${queryParams}`);
  }

  async assignTicketToClient(ticketId, clientId) {
    return this.request(`/tickets/${ticketId}/assign-to-client`, {
      method: 'POST',
      body: JSON.stringify({ clientId })
    });
  }

  async markTicketAsSpam(ticketId, reason = 'manual_mark') {
    return this.request(`/tickets/${ticketId}/mark-spam`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  async unmarkTicketAsSpam(ticketId) {
    return this.request(`/tickets/${ticketId}/unmark-spam`, {
      method: 'POST'
    });
  }

  // Ticket merge methods
  async mergeTickets(sourceTicketId, targetTicketId, mergeOptions = {}) {
    return this.request('/tickets/merge', {
      method: 'POST',
      body: JSON.stringify({ sourceTicketId, targetTicketId, mergeOptions })
    });
  }

  async getMergeCandidates(ticketId, limit = 10) {
    return this.request(`/tickets/merge-candidates/${ticketId}?limit=${limit}`);
  }

  // Check for tickets with recent email activity
  async getRecentEmailActivity(since = null) {
    const params = since ? `?since=${since.toISOString()}` : '';
    return this.request(`/tickets/recent-email-activity${params}`);
  }

  // Booking slot methods
  async getBookingSlots(params = {}) {
    return this.request('/bookings/my-slots', { params });
  }

  async createBookingSlots(slots) {
    return this.request('/bookings/create-slots', {
      method: 'POST',
      body: JSON.stringify({ slots })
    });
  }

  async updateBookingSlot(slotId, updates) {
    return this.request(`/bookings/slots/${slotId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteBookingSlot(slotId) {
    return this.request(`/bookings/slots/${slotId}`, {
      method: 'DELETE'
    });
  }

  async cancelBooking(slotId, reason) {
    return this.request(`/bookings/slots/${slotId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  async getBookingStats(params = {}) {
    return this.request('/bookings/stats', { params });
  }

  async getAvailableSlots(params = {}) {
    return this.request('/bookings/available-slots', { params });
  }

  async getBookingSlotByToken(token) {
    return this.request(`/bookings/slot/${token}`);
  }

  async bookSlot(bookingToken, clientData) {
    return this.request('/bookings/book-slot', {
      method: 'POST',
      body: JSON.stringify({ bookingToken, clientData })
    });
  }

  // Permission management methods
  async getPermissions() {
    return this.request('/permissions');
  }

  async getUserPermissions(userId) {
    return this.request(`/permissions/users/${userId}`);
  }

  async updateUserPermissions(userId, permissions, roleId) {
    return this.request(`/permissions/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ permissions, roleId })
    });
  }

  async getRolePermissions(roleId) {
    return this.request(`/permissions/roles/${roleId}`);
  }

  async updateRolePermissions(roleId, permissions) {
    return this.request(`/permissions/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify({ permissions })
    });
  }

  async bulkUpdatePermissions(updates) {
    return this.request('/permissions/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  }
}

const apiService = new ApiService();
export default apiService; 